import React from "react";

import classNames from "classnames";

import { ApolloError } from "@apollo/client";

import Loading from "ui/Loading";
import Panel from "ui/Panel";
import { ServerGroupMember } from "features/Server/ServerGroupForm/useServerGroupMembers";

type MemberRow = {
  member: ServerGroupMember;
  change: "added" | "removed" | "unchanged";
};

// The resolver does not order its members, so the panel sorts. Verifying a rule means
// checking it against a fleet you already know, which needs a stable, familiar order.
const byName = (rows: MemberRow[]) =>
  [...rows].sort((a, b) => a.member.name.localeCompare(b.member.name));

const CHANGE_STYLES: Record<MemberRow["change"], string> = {
  removed: "text-[#C22426]",
  added: "text-[#0F792B]",
  unchanged: "text-[#606060]",
};

const CHANGE_MARKERS: Record<MemberRow["change"], string> = {
  removed: "-",
  added: "+",
  unchanged: "",
};

type Props = {
  baselineMembers: ServerGroupMember[] | undefined;
  previewMembers: ServerGroupMember[] | undefined;
  loading: boolean;
  error?: ApolloError;
};

const PreviewDiff = ({
  baselineMembers,
  previewMembers,
  loading,
  error,
}: Props) => {
  const baselineIds = new Set(
    (baselineMembers ?? []).map((member) => member.id),
  );
  const previewIds = new Set((previewMembers ?? []).map((member) => member.id));
  const rows = byName([
    ...(baselineMembers ?? [])
      .filter((member) => !previewIds.has(member.id))
      .map((member) => ({ member, change: "removed" as const })),
    ...(previewMembers ?? []).map((member) => ({
      member,
      change: baselineIds.has(member.id)
        ? ("unchanged" as const)
        : ("added" as const),
    })),
  ]);
  const addedCount = rows.filter((row) => row.change === "added").length;
  const removedCount = rows.filter((row) => row.change === "removed").length;

  const renderBody = () => {
    if (error) {
      return <Loading error />;
    }
    if (baselineMembers === undefined || previewMembers === undefined) {
      return <Loading />;
    }
    if (rows.length === 0) {
      return <div className="text-[#606060]">No matching servers.</div>;
    }
    return (
      <ul className="list-none m-0 p-0">
        {rows.map(({ member, change }) => (
          <li key={member.id} className={CHANGE_STYLES[change]}>
            {/* Fixed-width marker so every name starts at the same x, markers or not */}
            <span className="inline-block w-[12px]">
              {CHANGE_MARKERS[change]}
            </span>
            {member.name}
          </li>
        ))}
      </ul>
    );
  };

  const title =
    previewMembers !== undefined
      ? `Live Preview - ${previewMembers.length} matching servers`
      : "Live Preview";

  const secondaryTitle = loading ? (
    "Updating..."
  ) : (
    <>
      {addedCount > 0 && <span className="text-[#0F792B]">+{addedCount}</span>}
      {removedCount > 0 && (
        <span className="text-[#C22426] ml-2">−{removedCount}</span>
      )}
    </>
  );

  return (
    <Panel title={title} secondaryTitle={secondaryTitle}>
      <div
        className={classNames(
          "p-[10px] transition-opacity",
          loading && "opacity-50",
        )}
      >
        {renderBody()}
      </div>
    </Panel>
  );
};

export default PreviewDiff;
